import React, { useState, useEffect, useRef } from 'react'
import { toast, ToastContainer } from 'react-toastify';
import { SmartAzanClock } from './scripts/SmartAzanClock';
import { AAA } from './data/Audios';
import { DefaultSettings } from './data/DefaultSettings';
import Menu from './components/Menu';
import Loading from './components/Loading';
import Clock from './components/Clock';
import AudioPlayer from './components/AudioPlayer';
import SilkSilentAudio from './components/SilkSilentAudio'
import { getTranslation, isRtlLanguage } from './data/I18n';
import { detectAutomaticLocation } from './scripts/Location';
import { startDailyAnalytics } from './scripts/Analytics';

export const AppContext = React.createContext();
let locationInitializationInProgress = false;

export default function AppContextProvider() {

    const [showMenu, setShowMenu] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [isSilkBrowser, setIsSilkBrowser] = useState(false);
    const [language, setLanguage] = useState(getStoredLanguage);
    const lastClockMinuteRef = useRef(null);

    const [output, setOutput] = useState(() => {

        let settings = JSON.parse(localStorage.getItem('settings'));
        if (!settings) {
            setShowLoading(true);
            initUser('new user - with no settings');
            return null;
        }
        else {
            return SmartAzanClock.run('existing user - first load');
        }

    })

    const t = (key) => getTranslation(language, key);

    useEffect(() => {
        const refreshForCurrentMinute = () => {
            let settings = JSON.parse(localStorage.getItem('settings'));
            if (!settings || settings.settingsVersion !== DefaultSettings.settingsVersion) {
                initUser('initUser: settings removed or upgraded');
            }
            else {
                const currentMinute = Math.floor(Date.now() / 60000);
                if (lastClockMinuteRef.current !== currentMinute) {
                    lastClockMinuteRef.current = currentMinute;
                    setOutput(SmartAzanClock.run('minute-change'));
                }
                setShowLoading(false);
            }
        };

        refreshForCurrentMinute();
        const interval = setInterval(refreshForCurrentMinute, 1000);
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = isRtlLanguage(language) ? 'rtl' : 'ltr';
        document.body.classList.toggle('app-rtl', isRtlLanguage(language));
    }, [language])

    useEffect(() => startDailyAnalytics(() => {
        try {
            return JSON.parse(localStorage.getItem('settings'));
        } catch (error) {
            return null;
        }
    }), [])

    useEffect(() => {
        const userAgent = navigator.userAgent || '';
        if (userAgent.includes('Silk')) {
            setIsSilkBrowser(true);
        }
        requestWakeLock();
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
                setOutput(SmartAzanClock.run('on-visible-again'));
            }
        });
    }, [])

    const showMsg = (msg, type) => {
        if (type === "error")
            toast.error(msg, { position: toast.POSITION.BOTTOM_RIGHT });
        else
            toast.success(msg, { position: toast.POSITION.BOTTOM_RIGHT });
    }

    // show a toast that stays on screen until the user closes it
    const showPersistentToast = (msg, options = {}) => {
        toast.info(msg, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            ...options
        });
    }

    const updateSettings = (kv, msg) => {
        let currentSettings = JSON.parse(localStorage.getItem('settings'));
        let newSettings = { ...currentSettings, ...kv }
        localStorage.setItem('settings', JSON.stringify({ ...newSettings }));
        if (kv.deviceSettings?.language) {
            setLanguage(kv.deviceSettings.language);
        }
        setOutput(SmartAzanClock.run("set" + JSON.stringify(kv)));
        if (msg)
            showMsg(msg);
    }

    const updateOffset = (vakit, op) => {

        let limit = 30;
        let currentSettings = JSON.parse(localStorage.getItem('settings'));
        let offsetSettings = currentSettings.offsetSettings;
        let newOffset = {};
        let finalOffsets = {}
        let newValue = offsetSettings[vakit] + (op === '+' ? 1 : -1)
        if (op === '0') {
            newValue = 0;
        }
        if (newValue > limit || newValue < -limit) {
            showMsg('Limit reached: ' + limit + ' minutes')
            return;
        }
        newOffset[vakit] = newValue
        finalOffsets['offsetSettings'] = { ...offsetSettings, ...newOffset };
        let newSettings = { ...currentSettings, ...finalOffsets }
        localStorage.setItem('settings', JSON.stringify({ ...newSettings }));
        setOutput(SmartAzanClock.run("set" + JSON.stringify(finalOffsets)));
    }

    const requestWakeLock = async () => {
        let wlock;
        try {
            wlock = await navigator.wakeLock.request('screen');
            dol('Screen Wake Lock is active');
        } catch (err) {
            /* console.error(err); */
        }
    };

    const previewAudio = (id) => {
        let AU = new AAA(id, output.time, false);
        localStorage.setItem("AAA", JSON.stringify({ ...AU }));
        setOutput(SmartAzanClock.run('audio preview'));
    }

    const reciteQuranAudio = (id) => {
        setTimeout(() => {
            localStorage.setItem("QuranAudio", JSON.stringify({ id: id }));
            setOutput(SmartAzanClock.run('Quran Recitation'))
        }, 3 * 1000)
        setShowMenu(false);
    }

    const refreshClock = () => {
        setOutput(SmartAzanClock.run('manual refresh'));
        requestWakeLock();
        setShowMenu(false);
    }

    return (
        <AppContext.Provider value={{
            showMenu, setShowMenu, showMsg, showPersistentToast, isAudioPlaying, setIsAudioPlaying,
            language, isRtl: isRtlLanguage(language), t,
            ...output, updateSettings, updateOffset, previewAudio, reciteQuranAudio, refreshClock, dol
        }}>
            {output ? <Clock /> : null}
            {showLoading ? <Loading /> : null}
            {output ? <Menu /> : null}
            {output ? <AudioPlayer /> : null}
            <ToastContainer autoClose={2000} limit={1} />
            {isSilkBrowser && <SilkSilentAudio />}
        </AppContext.Provider>
    )

}

const dol = (log) => { /* development only error */
    if (process.env.NODE_ENV === 'development')
        console.log(log + ' (dev only log)')
}

const getStoredLanguage = () => {
    try {
        return JSON.parse(localStorage.getItem('settings'))?.deviceSettings?.language
            || DefaultSettings.deviceSettings.language;
    } catch (e) {
        return DefaultSettings.deviceSettings.language;
    }
}

const initUser = (info) => {

    dol('InitUser: ' + (info ?? ''));

    let settings = { ...DefaultSettings };
    let currentSettings = JSON.parse(localStorage.getItem('settings'));

    if (isIOS()) {
        settings.deviceSettings.azanCallsEnabled = 'N';
    }

    if (currentSettings) {
        settings['locationSettings'] = { ...DefaultSettings.locationSettings, ...currentSettings.locationSettings }
        settings['calculationSettings'] = { ...DefaultSettings.calculationSettings, ...currentSettings.calculationSettings }
        settings['azanSettings'] = { ...DefaultSettings.azanSettings, ...currentSettings.azanSettings }
        settings['deviceSettings'] = { ...DefaultSettings.deviceSettings, ...currentSettings.deviceSettings }
        settings['offsetSettings'] = { ...DefaultSettings.offsetSettings, ...currentSettings.offsetSettings }
        settings['settingsVersion'] = DefaultSettings.settingsVersion;
        if ((currentSettings.settingsVersion || 0) < 18) {
            settings.deviceSettings.layout = DefaultSettings.deviceSettings.layout;
        }
        if ((currentSettings.settingsVersion || 0) < 19 && currentSettings.deviceSettings?.language === 'en') {
            settings.deviceSettings.language = DefaultSettings.deviceSettings.language;
        }
        if ((currentSettings.settingsVersion || 0) < 20
            && currentSettings.calculationSettings?.method === 'ISNA') {
            settings.calculationSettings.method = DefaultSettings.calculationSettings.method;
        }
        if (currentSettings.alarmSettings)
            settings['alarmSettings'] = currentSettings.alarmSettings;
        if (currentSettings.naflAlarmSettings)
            settings['naflAlarmSettings'] = currentSettings.naflAlarmSettings;
        localStorage.setItem('settings', JSON.stringify({ ...settings }));
        dol('Previous version carried over: ' + settings.locationSettings.address);
    }
    else {
        if (locationInitializationInProgress) return;
        locationInitializationInProgress = true;
        detectAutomaticLocation()
            .then((result) => {
                settings.locationSettings = { ...settings.locationSettings, ...result.locationSettings };
                dol(`Initiated using ${result.source}: ${settings.locationSettings.address}`);
            })
            .catch((error) => dol(error))
            .finally(() => {
                localStorage.setItem('settings', JSON.stringify({ ...settings }));
                locationInitializationInProgress = false;
            });
    }
}

const isIOS = () => {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
    ].includes(navigator.platform)
        // iPad on iOS 13 detection
        || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
}
